import { Capacitor, registerPlugin } from '@capacitor/core';

import { hasCapability } from 'src/platform';

export type WifiSecurity = 'OPEN' | 'WEP' | 'WPA' | 'WPA2' | 'WPA3' | 'UNKNOWN';

export type WifiNetwork = {
  ssid: string;
  signalLevel: number;
  security: WifiSecurity;
  isConnected: boolean;
};

type WifiScanResult = { networks: WifiNetwork[] };
type WifiConnectResult = { status: 'connected' | 'pending-user-approval' };

interface PointCraftWifiPlugin {
  scanNetworks(): Promise<WifiScanResult>;
  connectNetwork(options: {
    ssid: string;
    password?: string;
    security?: WifiSecurity;
  }): Promise<WifiConnectResult>;
  openSettings(): Promise<void>;
}

const PointCraftWifi = registerPlugin<PointCraftWifiPlugin>('PointCraftWifi');

export type WifiCapabilities = {
  platform: 'android' | 'ios' | 'web';
  canScan: boolean;
  canConnectKnownNetwork: boolean;
};

export function getWifiCapabilities(): WifiCapabilities {
  const platform = Capacitor.getPlatform();
  if (platform === 'android') {
    return { platform, canScan: true, canConnectKnownNetwork: true };
  }
  if (platform === 'ios') {
    return { platform, canScan: false, canConnectKnownNetwork: false };
  }
  return { platform: 'web', canScan: false, canConnectKnownNetwork: false };
}

export async function scanWifiNetworks(): Promise<WifiNetwork[]> {
  if (getWifiCapabilities().platform !== 'android') return [];
  const { networks } = await PointCraftWifi.scanNetworks();
  return networks;
}

export async function connectWifiNetwork(options: {
  ssid: string;
  password?: string;
  security?: WifiSecurity;
}): Promise<WifiConnectResult> {
  const capabilities = getWifiCapabilities();
  if (capabilities.platform === 'ios') {
    throw new Error(
      'iOS manages Wi-Fi connections in Settings. Open Settings to choose a network.'
    );
  }
  if (capabilities.platform === 'web' || !Capacitor.isNativePlatform()) {
    throw new Error('Web browsers cannot connect to Wi-Fi networks. Open your device settings.');
  }
  return PointCraftWifi.connectNetwork(options);
}

export async function openWifiSettings(): Promise<void> {
  // SITE_TRIAGE B4. `PointCraftWifi` is registered with NO implementations
  // (PLAN §0 fact F4), so on any runtime without Wi-Fi management this call
  // throws `UNIMPLEMENTED` instead of the friendly message below. The Electron
  // shell is "native" and would have hit exactly that.
  if (hasCapability('wifiManagement') === 'none') {
    throw new Error('Open Wi-Fi settings from your operating system.');
  }
  await PointCraftWifi.openSettings();
}
